import { GetStaticPaths, GetStaticProps } from 'next';

import { getServerPageGetVacancyDetail } from 'api/graphql-helper';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import {
	Enum_Componentinternaldefaultmoduleoptions_Padding,
	PageInformationFieldsFragment,
	VacancyDetailSingleFieldsFragment
} from 'api/graphql-types';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { BaseModule } from 'modules/base-module/base-module';

import { initialState as settingsInitialState } from 'redux/stores/settings';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import IntroHeader from 'modules/intro-header/intro-header';
import Share from 'modules/share/share';
import VacancyDetailText from 'modules/vacancy-detail-text/vacancy-detail-text';
import { Head } from 'modules/head/head';
import { FormattedVacancy, fetchFormatVacancy } from 'utils/fetch-format-vacancy';
import { fetchFormatVacancies } from 'utils/fetch-format-vacancies';

interface IVacancyDetailPageProps extends IBasePageProps {
	pageInformation?: PageInformationFieldsFragment;
	vacancyDetail?: VacancyDetailSingleFieldsFragment;
	vacancy: NonNullable<FormattedVacancy>;
	id?: string;
}

const VacancyDetailPage = (props: IVacancyDetailPageProps): React.JSX.Element => (
	<Layout navbar={props?.pageInformation?.navbar} footer={props?.pageInformation?.footer}>
		<Head
			seoModule={{
				title: `Vacature ${props.vacancy.title}`,
				description: props.vacancy.excerpt
			}}
		/>

		<BaseModule
			defaultModuleOptions={{
				backgroundColor: 'grey',
				padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
			}}
		>
			<IntroHeader
				module={{
					title: props.vacancy?.title || '',
					backgroundColor: 'grey',
					excerpt: props.vacancy?.excerpt || '',
					cover: {
						data: {
							attributes: {
								url: props.vacancy?.image || ''
							}
						}
					}
				}}
				slug={`/vacatures/${props.vacancy?.title}`}
			/>
		</BaseModule>

		<BaseModule
			defaultModuleOptions={{
				backgroundColor: 'white',
				padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
			}}
		>
			<VacancyDetailText vacancy={props.vacancy} border compact />
		</BaseModule>

		{props.vacancyDetail?.share && (
			<BaseModule defaultModuleOptions={props.vacancyDetail.share.defaultModuleOptions}>
				<Share module={props.vacancyDetail?.share} />
			</BaseModule>
		)}

		<ModuleBuilder modules={props?.vacancyDetail?.modules || []} slug="/vacatures" />
	</Layout>
);

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	const vacancies = await fetchFormatVacancies(undefined, 'public');

	const paths = vacancies?.map((vacancy: { id?: string }) => ({
		params: {
			id: vacancy.id || '/'
		},
		locale: ctx.defaultLocale
	}));

	return {
		paths: paths || [],
		fallback: 'blocking'
	};
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		const id = ctx.params?.id as string;
		const vacancy = await fetchFormatVacancy(id, 'public');

		const variables = {
			id: id,
			locale,
			publicationState
		};

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetVacancyDetail({ variables }, client);
		const { pageInformation, vacancyDetail } = result.props.data;

		const initialReduxState = {
			settings: {
				...settingsInitialState,
				publicationState,
				draftMode,
				contentType: 'vacancy',
				contentTypeId: null
			}
		};

		if (vacancy == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				key: id,
				pageInformation: pageInformation?.data?.attributes,
				vacancyDetail: vacancyDetail?.data?.attributes,
				id: id,
				vacancy,
				initialReduxState
			},
			revalidate: 600
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in vacancy detail', error);

		return {
			notFound: true
		};
	}
};

export default VacancyDetailPage;
